import { RootState } from '../store'
import { createSelector } from '@reduxjs/toolkit'
import { TeamMember } from './types'

// Team Member Details Selectors
export const selectTeamMembers = (state: RootState) => state.teamMember.teamMembers

export const selectTeamMemberFilters = (state: RootState) => state.teamMember.filters
export const selectSearchQuery = (state: RootState) => state.teamMember.searchQuery

export const selectTeamMemberInfo = (state: RootState) => state.teamMember.teamMemberInfo
export const selectLoadingTeamMemberInfo = (state: RootState) => state.teamMember.loadingTeamMemberInfo

export const selectActiveTeamMembers = createSelector(
  [selectTeamMembers],
  (teamMembers): TeamMember[] => {
    if (!Array.isArray(teamMembers)) return []
    return teamMembers
      .filter((member) => member.status === 'active')
      .sort((a, b) => {
        const aName = `${a.firstName} ${a.middleName || ''} ${a.lastName}`.toLowerCase()
        const bName = `${b.firstName} ${b.middleName || ''} ${b.lastName}`.toLowerCase()
        return aName.localeCompare(bName)
      })
  },
)

// Filtered Team Members Selector
export const selectFilteredTeamMembers = createSelector(
  [selectActiveTeamMembers, selectTeamMemberFilters, selectSearchQuery],
  (teamMembers, filters, searchQuery): TeamMember[] => {
    if (!Array.isArray(teamMembers)) return []
    return teamMembers.filter((member) => {
      const supervisorMatch =
        !filters.supervisor?.length || filters.supervisor.includes(member.supervisor.name)
      const operationalManagerMatch =
        !filters.operationalManager?.length ||
        filters.operationalManager.includes(member.operationalManager.name)
      const jobProfileMatch =
        !filters.jobProfile?.length || filters.jobProfile.includes(member.jobProfile)
      const practiceMatch = !filters.practice?.length || filters.practice.includes(member.practice)
      const isActive = member.status === 'active'

      const searchQueryLower = searchQuery.toLowerCase()
      const fullName =
        `${member.firstName} ${member.middleName} ${member.lastName} ${member.suffix}`.toLowerCase()

      const searchMatch =
        fullName.includes(searchQueryLower) || member.email.toLowerCase().includes(searchQueryLower)

      return (
        supervisorMatch &&
        operationalManagerMatch &&
        jobProfileMatch &&
        practiceMatch &&
        isActive &&
        searchMatch
      )
    })
  },
)

// Filter Options Selector
export const selectFilterOptions = createSelector([selectTeamMembers], (teamMembers) => {
  if (!Array.isArray(teamMembers))
    return {
      Practice: [],
      'Job Profile': [],
      Supervisor: [],
      'Operational Manager': [],
    }

  const practices = new Set(teamMembers.map((member) => member.practice))
  const jobProfiles = new Set(teamMembers.map((member) => member.jobProfile))
  const supervisors = new Set(teamMembers.map((member) => member.supervisor.name))
  const operationalManagers = new Set(teamMembers.map((member) => member.operationalManager.name))

  return {
    Practice: Array.from(practices).sort(),
    'Job Profile': Array.from(jobProfiles).sort(),
    Supervisor: Array.from(supervisors).sort(),
    'Operational Manager': Array.from(operationalManagers).sort(),
  }
})

// Team Member Points Selector
export const selectTeamMemberPoints = (state: RootState) => state.teamMember.teamMemberPoints

// Loading Selectors
export const selectLoadingTeamMembers = (state: RootState) => state.teamMember.loadingTeamMembers
export const selectLoadingTeamMemberPoints = (state: RootState) =>
  state.teamMember.loadingTeamMemberPoints

// Error Selectors
export const selectErrorTeamMembers = (state: RootState) => state.teamMember.errorTeamMembers
export const selectErrorTeamMemberPoints = (state: RootState) => state.teamMember.errorTeamMemberPoints

// Sync Counts Selector
export const selectSyncCounts = (state: RootState) =>
  state.teamMember.syncCounts || {
    updatedCount: 0,
    addedCount: 0,
    terminatedCount: 0,
  }
